"use client";

import { useState, type CSSProperties, type ReactNode } from "react";
import { Heading, Icon } from "@farm-junction/design-library";
import ImageWrapper from "@/component/ui/ImageWrapper";
import CarImageCardModal from "@/component/ui/CarImageCard/CarImageCardModal";
import type { GalleryImage, ImageGallerySectionData } from "@/app/utilities/interfaces";

export type ImageGallerySectionLibImage = GalleryImage & {
    title?: string;
    label?: string;
};

export type ImageGallerySectionLibData = Omit<ImageGallerySectionData, "images"> & {
    images: ImageGallerySectionLibImage[];
};

type ImageGallerySectionLibProps = {
    data: ImageGallerySectionLibData | ImageGallerySectionData;
    imageCount?: number;
    hideHeader?: boolean;
    /** Heading tag for the section title. Defaults to h3. */
    headingLevel?: 2 | 3;
    className?: string;
    onImageClick?: (index: number, image: ImageGallerySectionLibImage) => void;
    onViewAllClick?: () => void;
};

const TILE_CLASS = "relative h-full w-full overflow-hidden bg-[#F4F4F4] cursor-pointer focus:outline-none z-9";

const getDesktopGalleryGridStyle = (count: number): CSSProperties => {
    if (count <= 1) {
        return {
            gridTemplateColumns: "1fr",
            gridTemplateRows: "360px",
            gridTemplateAreas: `"hero"`,
        };
    }

    if (count === 2) {
        return {
            gridTemplateColumns: "1fr 1fr",
            gridTemplateRows: "360px",
            gridTemplateAreas: `"hero rt"`,
        };
    }

    if (count === 3) {
        return {
            gridTemplateColumns: "2fr 1fr",
            gridTemplateRows: "180px 180px",
            gridTemplateAreas: `
                "hero rt"
                "hero rb"
            `,
        };
    }

    if (count === 4) {
        return {
            gridTemplateColumns: "1fr 1fr",
            gridTemplateRows: "1fr 1fr",
            aspectRatio: "2 / 1",
            gridTemplateAreas: `
                "hero rt"
                "rb bl"
            `,
        };
    }

    if (count === 5) {
        return {
            gridTemplateColumns: "repeat(6, 1fr)",
            gridTemplateRows: "220px 180px",
            gridTemplateAreas: `
                "hero hero hero rt rt rt"
                "rb rb bl bl bc bc"
            `,
        };
    }

    return {
        gridTemplateColumns: "repeat(3, 1fr)",
        gridTemplateRows: "180px 180px 180px",
        gridTemplateAreas: `
            "hero hero rt"
            "hero hero rb"
            "bl   bc   va"
        `,
    };
};

function GalleryTile({
    image,
    sizes,
    onClick,
    overlay,
    showLabel = true,
    children,
    className = "",
}: {
    image: ImageGallerySectionLibImage;
    sizes: string;
    onClick?: () => void;
    overlay?: boolean;
    showLabel?: boolean;
    children?: ReactNode;
    className?: string;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={image.title || image.alt}
            title={image.title || image.alt}
            className={`${TILE_CLASS} ${className}`}
        >
            <ImageWrapper
                src={image.src}
                alt={image.alt}
                title={image.title || image.alt}
                sizes={sizes}
                className={`object-cover ${overlay ? "opacity-40" : ""}`}
            />
            {showLabel && image.label && !overlay && (
                <span className="pointer-events-none absolute bottom-2 left-2 z-9 rounded-md bg-black/55 px-2 py-0.5 text-[11px] font-medium text-white md:bottom-3 md:left-3 md:px-2.5 md:text-xs">
                    {image.label}
                </span>
            )}
            {children}
        </button>
    );
}

export default function ImageGallerySectionLib({
    data,
    imageCount,
    hideHeader = false,
    headingLevel = 3,
    className = "",
    onImageClick,
    onViewAllClick,
}: ImageGallerySectionLibProps) {
    const { title, description, viewAllLabel, images } = data;
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

    if (!images?.length) return null;

    const sectionCount = images.length;
    const totalCount = imageCount ?? sectionCount;
    const displayImages = images.slice(0, 6);
    const showViewAll = sectionCount > 6;
    const viewAllIndex = showViewAll ? displayImages.length - 1 : -1;
    const desktopGridStyle = getDesktopGalleryGridStyle(showViewAll ? 6 : displayImages.length);

    const openAt = (index: number) => {
        const image = images[index];
        if (!image) return;
        if (onImageClick) {
            onImageClick(index, image);
            return;
        }
        setSelectedIndex(index);
    };

    const openViewAll = () => {
        if (onViewAllClick) {
            onViewAllClick();
            return;
        }
        openAt(0);
    };

    const isViewAllTile = (index: number) => index === viewAllIndex;

    const viewAllOverlay = (
        <span className="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center gap-1 bg-black/45 text-white">
            <span className="flex items-center gap-1.5 text-sm font-bold md:text-lg">
                {viewAllLabel || "View All Images"}
                <Icon name="rightCaret" width={16} height={16} fill="#ffffff" />
            </span>
            <span className="text-[11px] opacity-90 md:text-xs">{sectionCount} Images</span>
        </span>
    );

    return (
        <section className={`w-full ${className}`}>
            {!hideHeader && (
                <div className="mb-4 md:mb-6">
                    <Heading
                        variant={headingLevel === 2 ? "h2" : "h3"}
                        level={headingLevel}
                        className="!tracking-[0px] mb-1 !text-[20px] !font-bold !leading-[24px] !md:leading-[36px] text-gray-900"
                    >
                        {title}
                    </Heading>
                    {description && <p className="mt-1 text-sm text-gray-500">{description}</p>}
                </div>
            )}

            <div
                className="hidden overflow-hidden rounded-[16px] border border-gray-200 md:grid md:gap-3"
                style={desktopGridStyle}
            >
                {images[0]?.src && (
                    <div className="relative h-full min-h-0" style={{ gridArea: "hero" }}>
                        <GalleryTile
                            image={images[0]}
                            sizes="66vw"
                            overlay={isViewAllTile(0)}
                            onClick={() => (isViewAllTile(0) ? openViewAll() : openAt(0))}
                        >
                            {isViewAllTile(0) ? viewAllOverlay : null}
                        </GalleryTile>
                    </div>
                )}
                {images[1]?.src && (
                    <div className="relative h-full min-h-0" style={{ gridArea: "rt" }}>
                        <GalleryTile
                            image={images[1]}
                            sizes="33vw"
                            overlay={isViewAllTile(1)}
                            onClick={() => (isViewAllTile(1) ? openViewAll() : openAt(1))}
                        >
                            {isViewAllTile(1) ? viewAllOverlay : null}
                        </GalleryTile>
                    </div>
                )}
                {images[2]?.src && (
                    <div className="relative h-full min-h-0" style={{ gridArea: "rb" }}>
                        <GalleryTile
                            image={images[2]}
                            sizes="33vw"
                            overlay={isViewAllTile(2)}
                            onClick={() => (isViewAllTile(2) ? openViewAll() : openAt(2))}
                        >
                            {isViewAllTile(2) ? viewAllOverlay : null}
                        </GalleryTile>
                    </div>
                )}
                {images[3]?.src && (
                    <div className="relative h-full min-h-0" style={{ gridArea: "bl" }}>
                        <GalleryTile
                            image={images[3]}
                            sizes="33vw"
                            overlay={isViewAllTile(3)}
                            onClick={() => (isViewAllTile(3) ? openViewAll() : openAt(3))}
                        >
                            {isViewAllTile(3) ? viewAllOverlay : null}
                        </GalleryTile>
                    </div>
                )}
                {images[4]?.src && (
                    <div className="relative h-full min-h-0" style={{ gridArea: "bc" }}>
                        <GalleryTile
                            image={images[4]}
                            sizes="33vw"
                            overlay={isViewAllTile(4)}
                            onClick={() => (isViewAllTile(4) ? openViewAll() : openAt(4))}
                        >
                            {isViewAllTile(4) ? viewAllOverlay : null}
                        </GalleryTile>
                    </div>
                )}
                {images[5]?.src && (
                    <div className="relative h-full min-h-0" style={{ gridArea: "va" }}>
                        <GalleryTile
                            image={images[5]}
                            sizes="33vw"
                            overlay={isViewAllTile(5)}
                            onClick={() => (isViewAllTile(5) ? openViewAll() : openAt(5))}
                        >
                            {isViewAllTile(5) ? viewAllOverlay : null}
                        </GalleryTile>
                    </div>
                )}
            </div>

            <div className="flex flex-col gap-3 md:hidden">
                {images[0]?.src && (
                    <div className="h-[220px] overflow-hidden rounded-2xl">
                        <GalleryTile image={images[0]} sizes="100vw" onClick={() => openAt(0)} />
                    </div>
                )}
                {images.length > 1 && (
                    <div className="grid grid-cols-4 gap-2">
                        {images.slice(1, 5).map((image, index, thumbs) => {
                            const originalIndex = index + 1;
                            const showOverlay = showViewAll && index === thumbs.length - 1;

                            return (
                                <div key={image.id} className="relative h-[64px] overflow-hidden rounded-xl">
                                    <GalleryTile
                                        image={image}
                                        sizes="25vw"
                                        showLabel={false}
                                        overlay={showOverlay}
                                        onClick={() => (showOverlay ? openViewAll() : openAt(originalIndex))}
                                    >
                                        {showOverlay ? (
                                            <span className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-black/45 p-1 text-center">
                                                <span className="text-[10px] font-bold leading-tight text-white">
                                                    {viewAllLabel || "View All Images"}
                                                    <br />
                                                    ({sectionCount})
                                                </span>
                                            </span>
                                        ) : null}
                                    </GalleryTile>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {!onImageClick && (
                <CarImageCardModal
                    open={selectedIndex !== null}
                    data={{
                        title,
                        images: images.map((image) => image.src),
                        imageCount: totalCount,
                    }}
                    startIndex={selectedIndex ?? 0}
                    onClose={() => setSelectedIndex(null)}
                />
            )}
        </section>
    );
}
